/**
 * CoordinatePlane component for rendering coordinate plane visualizations
 * Follows the coordinate_agent_prompt.md specifications:
 * - Uses pure HTML + inline SVG only (no external libraries)
 * - Standard configuration parameters
 * - Standard color scheme
 * - Grade-level appropriate rendering
 */
import React from 'react';
import { Box, Typography } from '@mui/material';

interface Point {
  label: string;
  x: number;
  y: number;
  color?: string;
}

interface Line {
  from: string;
  to: string;
  color?: string;
  style?: 'solid' | 'dashed';
}

interface GridSize {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface CoordinatePlaneConfig {
  gridSize: GridSize;
  points: Point[];
  lines?: Line[];
  showGrid?: boolean;
  showAxisLabels?: boolean;
  showNumberLabels?: boolean;
  showCoordinateLines?: boolean;
  title?: string;
  description?: string;
}

interface CoordinatePlaneProps {
  config: CoordinatePlaneConfig;
  width?: number;
  height?: number;
}

// Standard color scheme from the prompt
const STANDARD_COLORS = {
  pointA: '#e74c3c', // red
  pointB: '#3498db', // blue
  pointC: '#2ecc71', // green
  pointD: '#f39c12', // orange
  lineSolid: '#2ecc71', // green
  lineDashed: '#95a5a6', // gray
};

const CoordinatePlane: React.FC<CoordinatePlaneProps> = ({ 
  config, 
  width = 600, 
  height = 600 
}) => {
  const margin = 50;
  const plotWidth = width - 2 * margin;
  const plotHeight = height - 2 * margin;
  const { minX, maxX, minY, maxY } = config.gridSize;
  const xRange = maxX - minX;
  const yRange = maxY - minY;
  const scaleX = plotWidth / xRange;
  const scaleY = plotHeight / yRange;

  const toSVGX = (x: number) => margin + (x - minX) * scaleX;
  const toSVGY = (y: number) => height - margin - (y - minY) * scaleY;

  // Generate grid lines
  const gridLines: JSX.Element[] = [];
  if (config.showGrid !== false) {
    for (let x = minX; x <= maxX; x++) {
      const svgX = toSVGX(x);
      gridLines.push(
        <line
          key={`grid-x-${x}`}
          x1={svgX}
          y1={margin}
          x2={svgX}
          y2={height - margin}
          stroke="#e0e0e0"
          strokeWidth="1"
        />
      );
    }
    for (let y = minY; y <= maxY; y++) {
      const svgY = toSVGY(y);
      gridLines.push(
        <line
          key={`grid-y-${y}`}
          x1={margin}
          y1={svgY}
          x2={width - margin}
          y2={svgY}
          stroke="#e0e0e0"
          strokeWidth="1"
        />
      );
    }
  }

  // Generate number labels
  const numberLabels: JSX.Element[] = [];
  if (config.showNumberLabels !== false) {
    for (let x = minX; x <= maxX; x++) {
      numberLabels.push(
        <text
          key={`label-x-${x}`}
          x={toSVGX(x)}
          y={height - margin + 20}
          fontSize="12"
          textAnchor="middle"
        >
          {x}
        </text>
      );
    }
    for (let y = minY; y <= maxY; y++) {
      numberLabels.push(
        <text
          key={`label-y-${y}`}
          x={margin - 15}
          y={toSVGY(y) + 5}
          fontSize="12"
          textAnchor="end"
        >
          {y}
        </text>
      );
    }
  }

  // Generate lines between points
  const lines: JSX.Element[] = [];
  if (config.lines) {
    config.lines.forEach((line, index) => {
      const pFrom = config.points.find(p => p.label === line.from);
      const pTo = config.points.find(p => p.label === line.to);
      if (pFrom && pTo) {
        const lineColor = line.color || (line.style === 'dashed' ? STANDARD_COLORS.lineDashed : STANDARD_COLORS.lineSolid);
        lines.push(
          <line
            key={`line-${index}`}
            x1={toSVGX(pFrom.x)}
            y1={toSVGY(pFrom.y)}
            x2={toSVGX(pTo.x)}
            y2={toSVGY(pTo.y)}
            stroke={lineColor}
            strokeWidth="2"
            strokeDasharray={line.style === 'dashed' ? '5,5' : undefined}
          />
        );
      }
    });
  }

  // Generate points with coordinate lines
  const pointElements: JSX.Element[] = [];
  config.points.forEach((point, index) => {
    // Determine color based on standard scheme or provided color
    let pointColor = point.color;
    if (!pointColor) {
      switch (index) {
        case 0:
          pointColor = STANDARD_COLORS.pointA;
          break;
        case 1:
          pointColor = STANDARD_COLORS.pointB;
          break;
        case 2:
          pointColor = STANDARD_COLORS.pointC;
          break;
        case 3:
          pointColor = STANDARD_COLORS.pointD;
          break;
        default:
          pointColor = STANDARD_COLORS.pointA;
      }
    }

    const svgX = toSVGX(point.x);
    const svgY = toSVGY(point.y);

    // Coordinate lines (dotted lines from point to axes)
    if (config.showCoordinateLines !== false) {
      pointElements.push(
        <line
          key={`coord-x-${index}`}
          x1={svgX}
          y1={svgY}
          x2={svgX}
          y2={height - margin}
          stroke={pointColor}
          strokeWidth="1"
          strokeDasharray="5,5"
          opacity="0.5"
        />,
        <line
          key={`coord-y-${index}`}
          x1={margin}
          y1={svgY}
          x2={svgX}
          y2={svgY}
          stroke={pointColor}
          strokeWidth="1"
          strokeDasharray="5,5"
          opacity="0.5"
        />
      );
    }

    // Point circle and label
    pointElements.push(
      <circle
        key={`point-${index}`}
        cx={svgX}
        cy={svgY}
        r="8"
        fill={pointColor}
      />,
      <text
        key={`point-label-${index}`}
        x={svgX + 15}
        y={svgY - 10}
        fontSize="14"
        fontWeight="bold"
        fill={pointColor}
      >
        {point.label}({point.x}, {point.y})
      </text>
    );
  });

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center',
      bgcolor: 'white',
      p: 2,
      borderRadius: 1,
      border: '2px solid #3498db',
    }}>
      {config.title && (
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#2c3e50', 
            mb: 1,
            fontWeight: 600
          }}
        >
          {config.title}
        </Typography>
      )}
      {config.description && (
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#34495e', 
            mb: 2,
            fontSize: '16px'
          }}
        >
          {config.description}
        </Typography>
      )}
      <svg
        width={width}
        height={height}
        style={{
          border: '2px solid #3498db',
          borderRadius: '5px',
          backgroundColor: 'white',
        }}
      >
        {/* Grid lines */}
        {gridLines}
        
        {/* Axes */}
        <line
          x1={margin}
          y1={margin}
          x2={margin}
          y2={height - margin}
          stroke="#2c3e50"
          strokeWidth="2"
        />
        <line
          x1={margin}
          y1={height - margin}
          x2={width - margin}
          y2={height - margin}
          stroke="#2c3e50"
          strokeWidth="2"
        />
        
        {/* Arrows */}
        <polygon
          points={`${margin},${margin - 5} ${margin - 5},${margin + 5} ${margin + 5},${margin + 5}`}
          fill="#2c3e50"
        />
        <polygon
          points={`${width - margin + 5},${height - margin} ${width - margin - 5},${height - margin - 5} ${width - margin - 5},${height - margin + 5}`}
          fill="#2c3e50"
        />
        
        {/* Axis labels */}
        {config.showAxisLabels !== false && (
          <>
            <text
              x={width - 20}
              y={height - margin + 25}
              fontSize="14"
              fontWeight="bold"
              fill="#2c3e50"
            >
              x
            </text>
            <text
              x={margin - 25}
              y={margin - 10}
              fontSize="14"
              fontWeight="bold"
              fill="#2c3e50"
            >
              y
            </text>
          </>
        )}
        
        {/* Number labels */}
        {numberLabels}
        
        {/* Lines between points */}
        {lines}
        
        {/* Points with coordinate lines */}
        {pointElements}
      </svg>
    </Box>
  );
};

export default CoordinatePlane;
